<template>
  <div class="min-h-screen bg-white">
    <BasicNavbar />
    <div
      class="w-full h-full flex justify-center mt-10 tablet-v:mt-[56px] desktop:mt-[126px] px-3 pb-3"
    >
      <div class="w-full max-w-[440px]">
        <!-- Header -->
        <div class="flex items-center justify-between mb-[56px]">
          <div
            class="font-primary font-medium text-[26px] leading-[22px] tracking-[-0.38px] text-gray-800"
          >
            Add organization
          </div>
          <RouterLink
            :to="nextRoute"
            class="font-primary text-[14px] leading-[18px] mt-1 text-green-900"
            :class="skipClasses"
          >
            Skip
          </RouterLink>
        </div>

        <!-- Form -->
        <div class="mb-[14px]">
          <SearchOrganizations
            v-model="selectedOrg"
            label="Organization name (optional)"
            :disabled="loading"
            @update:query="handleNameUpdate"
          />
        </div>
        <div
          class="font-secondary text-[13px] leading-[18px] text-gray-800 mb-5"
        >
          Add an organization if you plan to add for copyright protection on
          their behalf.
        </div>
        <UiButton
          class="w-full!"
          :loading="loading"
          primary
          @click="handleSubmit"
        >
          Get started
        </UiButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import BasicNavbar from '@/shared/components/BasicNavbar.vue'
import SearchOrganizations from '@/features/search/components/SearchOrganizations.vue'
import { UiButton } from '@dacr/ui'
import {
  CopyrightOwnersNewDocument,
  type CopyrightOwnersNewQuery,
  CopyrightOwnerType,
  EntityType,
  type Organization,
  useAddCopyrightOwnerEntityMutation,
  useAddCopyrightOwnerMutation,
} from '@/shared/graphql/legacy/generated'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useFeatureFlag } from '@/shared/composables/useFeatureFlag'
import { FeatureFlag } from '@/shared/stores/featureFlags'

const { mutate: addCopyrightOwner, loading: loadingAddCoprightOwner } =
  useAddCopyrightOwnerMutation()
const {
  mutate: addCopyrightOwnerEntity,
  loading: loadingAddCoprightOwnerEntity,
} = useAddCopyrightOwnerEntityMutation()

const router = useRouter()
const toastStore = useToastStore()
const originFlagEnabled = useFeatureFlag(FeatureFlag.Origin)

const organizationName = ref('')
const selectedOrg = ref<Organization>()
const errorMessage = ref('')

const loading = computed(
  () => loadingAddCoprightOwner.value || loadingAddCoprightOwnerEntity.value
)

const skipClasses = computed(() => ({
  ...(loading.value && {
    'opacity-50': true,
    'pointer-events-none': true,
  }),
}))

const nextRoute = computed(() =>
  originFlagEnabled.value ? '/identity' : '/onboarding'
)

const handleDone = () => {
  window.scrollTo(0, 0)
}

const handleNameUpdate = (value: string) => {
  organizationName.value = value
}

const handleSubmit = async () => {
  handleDone()

  if (!organizationName.value && !selectedOrg.value) {
    errorMessage.value = 'Please enter an organization name'
    return
  }

  try {
    if (organizationName.value) {
      await addCopyrightOwner(
        {
          name: organizationName.value,
          type: CopyrightOwnerType.Organization,
        },
        {
          update(cache, { data }) {
            cache.updateQuery<CopyrightOwnersNewQuery>(
              {
                query: CopyrightOwnersNewDocument,
              },
              (response) => {
                if (!response || !data) {
                  return
                }

                if (!response && data) {
                  return {
                    __typename: 'Query',
                    copyrightOwnersNew: {
                      __typename: 'CopyrightOwnersResponseNew',
                      items: [
                        {
                          __typename: 'CopyrightOwnerNew',
                          id: Date.now().toString(),
                          name: data.addCopyrightOwnerNew.name,
                          avatarUrl: data.addCopyrightOwnerNew.avatarUrl,
                          isSelected: data.addCopyrightOwnerNew.isSelected,
                          type: data.addCopyrightOwnerNew.type,
                          owner: data.addCopyrightOwnerNew.owner,
                        },
                      ],
                      total: 1,
                      next: null,
                    },
                  }
                }

                return {
                  ...response,
                  copyrightOwnersNew: {
                    ...response.copyrightOwnersNew,
                    items: response.copyrightOwnersNew.items.concat({
                      __typename: 'CopyrightOwnerNew',
                      id: Date.now().toString(),
                      name: data.addCopyrightOwnerNew.name,
                      avatarUrl: data.addCopyrightOwnerNew.avatarUrl,
                      isSelected: data.addCopyrightOwnerNew.isSelected,
                      type: data.addCopyrightOwnerNew.type,
                      owner: data.addCopyrightOwnerNew.owner,
                    }),
                  },
                }
              }
            )
          },
        }
      )
    }

    if (selectedOrg.value) {
      await addCopyrightOwnerEntity(
        {
          input: {
            id: selectedOrg.value.id,
            type: EntityType.Organization,
          },
        },
        {
          update(cache, { data }) {
            cache.updateQuery<CopyrightOwnersNewQuery>(
              {
                query: CopyrightOwnersNewDocument,
              },
              (response) => {
                if (!response || !data) {
                  return
                }

                if (!response && data) {
                  return {
                    __typename: 'Query',
                    copyrightOwnersNew: {
                      __typename: 'CopyrightOwnersResponseNew',
                      items: [
                        {
                          __typename: 'CopyrightOwnerNew',
                          id: Date.now().toString(),
                          name: data.addCopyrightOwnerEntityNew.name,
                          avatarUrl: data.addCopyrightOwnerEntityNew.avatarUrl,
                          isSelected:
                            data.addCopyrightOwnerEntityNew.isSelected,
                          type: data.addCopyrightOwnerEntityNew.type,
                          owner: data.addCopyrightOwnerEntityNew.owner,
                        },
                      ],
                      total: 1,
                      next: null,
                    },
                  }
                }

                return {
                  ...response,
                  copyrightOwnersNew: {
                    ...response.copyrightOwnersNew,
                    items: response.copyrightOwnersNew.items.concat({
                      __typename: 'CopyrightOwnerNew',
                      id: Date.now().toString(),
                      name: data.addCopyrightOwnerEntityNew.name,
                      avatarUrl: data.addCopyrightOwnerEntityNew.avatarUrl,
                      isSelected: data.addCopyrightOwnerEntityNew.isSelected,
                      type: data.addCopyrightOwnerEntityNew.type,
                      owner: data.addCopyrightOwnerEntityNew.owner,
                    }),
                  },
                }
              }
            )
          },
        }
      )
    }
  } catch (error) {
    console.error(error)

    toastStore.showToast({
      type: ToastType.Error,
      content: 'Something went wrong - please try again later.',
      error: error as Error,
    })
  }

  return router.push(nextRoute.value)
}
</script>
